// client/src/api/events/eventsApi.ts
//
// Dominio: Events (creazione, gestione, partecipazione, notifiche, inviti pubblici,
// chiusura evento + dichiarazione km).
// Lift-and-shift FEDELE da services/apiClient.ts (blocco EVENTS) e
// services/eventsService.ts (closeEvent + declareKm).
// Nessuna logica riscritta, nessun contratto API modificato. Le firme
// e i return type ricalcano 1:1 l'originale per garantire retrocompatibilità
// totale con la UI esistente.
//
// Dipende da: @/api/core/boundApiFetch (wrapper con store iniettato).

import { boundApiFetch } from "@/api/core/boundApiFetch";
import type { Event, EventNotification } from "@/types";

// =====================
// TIPI DI RISPOSTA
// =====================

/**
 * Risposta delle mutazioni evento: il server restituisce sempre `ok`, spesso
 * l'evento aggiornato e, in alcuni casi, campi di contorno (inviti, conteggi).
 */
export interface EventMutationResult {
  ok?: boolean;
  event?: Event;
  [key: string]: unknown;
}

export interface CreateEventResult {
  event: Event;
  invitations_sent?: number;
  invitations_failed?: string[];
  is_draft?: boolean;
  message?: string;
}

export interface CreateEventPayload {
  chapter_id?: string | null;
  route_id?: string | null;
  title: string;
  description?: string;
  event_date: string;
  end_datetime?: string;
  is_draft?: boolean;
  chapter_only?: boolean;
  cover_image_url?: string;
  has_photos?: boolean;
  visibility?: "public" | "chapter" | "private" | "shared";
  province?: string | null;
  bike_type?: string | null;
  target_brand?: string | null;
  meeting_point?: Record<string, unknown> | null;
  max_participants?: number | null;
  invites?: string[];
}

export interface JoinEventResult {
  participation?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface EventInviteResult {
  event?: Event;
  invite?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface ActiveEventResult {
  event?: Event | null;
  [key: string]: unknown;
}

// =====================
// EVENTS
// =====================

export async function getEvents() {
  return boundApiFetch<{ events: Event[] }>("/api/events");
}

export async function getEventById(eventId: string) {
  return boundApiFetch<EventMutationResult>(`/api/events/${eventId}`);
}

export async function createEvent(payload: CreateEventPayload) {
  return boundApiFetch<CreateEventResult>(`/api/events`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateEvent(eventId: string, patch: Record<string, unknown>) {
  return boundApiFetch<EventMutationResult>(`/api/events/${eventId}`, {
    method: "PUT",
    body: JSON.stringify(patch),
  });
}

export async function deleteEvent(eventId: string) {
  return boundApiFetch<EventMutationResult>(`/api/events/${eventId}`, {
    method: "DELETE",
  });
}

export async function cancelEvent(
  eventId: string,
): Promise<EventMutationResult> {
  return boundApiFetch<EventMutationResult>(`/api/events/${eventId}/cancel`, {
    method: "POST",
  });
}

export async function updateEventVisibility(
  eventId: string,
  visibility: "public" | "chapter" | "private" | "shared",
): Promise<EventMutationResult> {
  return boundApiFetch<EventMutationResult>(
    `/api/events/${eventId}/update-visibility`,
    {
      method: "PUT",
      body: JSON.stringify({ visibility }),
    },
  );
}

export async function addEventDate(
  eventId: string,
  eventDate: string,
): Promise<EventMutationResult> {
  return boundApiFetch<EventMutationResult>(`/api/events/${eventId}/add-date`, {
    method: "POST",
    body: JSON.stringify({ event_date: eventDate }),
  });
}

export async function sendEventInvites(
  eventId: string,
  invites: string[],
): Promise<{ success: boolean; invitations_sent: number; failed?: string[] }> {
  // Detect if all items look like UUIDs → send as user_ids for precise matching
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const allUuids = invites.every((s) => uuidRegex.test(s));
  const body = allUuids
    ? { user_ids: invites }
    : { invites };
  return boundApiFetch<{
    success: boolean;
    invitations_sent: number;
    failed?: string[];
  }>(`/api/events/${eventId}/send-invites`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// =====================
// EVENTS — PARTICIPATION
// =====================

export async function joinEvent(
  eventId: string,
  bikeId?: string | null,
  partySize?: number | null,
) {
  const payload: Record<string, unknown> = {};
  if (bikeId) payload.bike_id = bikeId;
  if (typeof partySize === "number" && Number.isFinite(partySize))
    payload.party_size = partySize;
  const body = Object.keys(payload).length ? JSON.stringify(payload) : undefined;
  return boundApiFetch<JoinEventResult>(`/api/events/${eventId}/join`, {
    method: "POST",
    body,
  });
}

export async function leaveEvent(eventId: string): Promise<void> {
  await boundApiFetch(`/api/events/${eventId}/leave`, { method: "DELETE" });
}

export async function respondToEventInvitation(
  invitationId: string,
  action: "accepted" | "rejected",
  party_size?: number,
): Promise<{ ok: boolean; status: string }> {
  return boundApiFetch<{ ok: boolean; status: string }>(
    `/api/events/${invitationId}/respond`,
    {
      method: "PATCH",
      body: JSON.stringify({
        action,
        ...(party_size ? { party_size } : {}),
      }),
    },
  );
}

export async function getActiveEvent() {
  return boundApiFetch<ActiveEventResult>("/api/events/active");
}

// =====================
// EVENT NOTIFICATIONS
// =====================

export async function getEventNotificationsUnreadCount(): Promise<{
  unread: number;
}> {
  return boundApiFetch<{ unread: number }>(
    "/api/events/notifications/unread-count",
  );
}

export async function getEventNotifications(
  limit = 30,
  unreadOnly = false,
): Promise<{ unread: number; notifications: EventNotification[] }> {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (unreadOnly) qs.set("unread_only", "1");
  return boundApiFetch<{ unread: number; notifications: EventNotification[] }>(
    `/api/events/notifications?${qs.toString()}`,
  );
}

export async function markEventNotificationsRead(
  ids: string[],
): Promise<{ ok: true; unread: number }> {
  return boundApiFetch<{ ok: true; unread: number }>(
    "/api/events/notifications/mark-read",
    {
      method: "POST",
      body: JSON.stringify({ ids }),
    },
  );
}

// =====================
// EVENTS — PUBLIC INVITES
// =====================

export async function getEventInvite(token: string) {
  return boundApiFetch<EventInviteResult>(
    `/api/public/event-invite/${encodeURIComponent(token)}`,
  );
}

export async function joinEventInvite(
  token: string,
  payload: { bike_id?: string | null; party_size?: number | null } = {},
) {
  return boundApiFetch<JoinEventResult>(
    `/api/public/event-invite/${encodeURIComponent(token)}/join`,
    {
      method: "POST",
      body: JSON.stringify(payload || {}),
    },
  );
}

// =====================
// EVENTS — CLOSE + DECLARE KM (ex services/eventsService.ts)
// =====================

export interface ParticipantKm {
  user_id: string;
  km: number | null;
}

export interface CloseEventPayload {
  /** Nome canonico del server (alias storici accettati: official_km). */
  actual_km?: number | null;
  official_km?: number | null;
  /** Nome canonico del server (alias storici: participant_km, member_km_declarations). */
  participants_km?: ParticipantKm[];
  /** @deprecated usare participants_km — accettato dal server per retro-compatibilità. */
  participant_km?: ParticipantKm[];
}

export interface CloseEventResponse {
  success: boolean;
  event_id: string;
  actual_km: number | null;
}

export interface DeclareKmPayload {
  km: number;
}

export interface DeclareKmResponse {
  success: boolean;
  km: number;
}

/**
 * POST /api/events/:id/close
 * Chiude l'evento e registra i km percorsi (solo creator / chapter admin).
 */
export async function closeEvent(
  eventId: string,
  payload: CloseEventPayload,
): Promise<CloseEventResponse> {
  return boundApiFetch<CloseEventResponse>(`/api/events/${eventId}/close`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/**
 * POST /api/events/:id/declare-km
 * Dichiara i km percorsi dal partecipante autenticato.
 */
export async function declareKm(
  eventId: string,
  payload: DeclareKmPayload,
): Promise<DeclareKmResponse> {
  return boundApiFetch<DeclareKmResponse>(
    `/api/events/${eventId}/declare-km`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}